/**
 * The canonical Lumos edit recipe.
 *
 * One recipe fully describes every pixel difference between the immutable
 * source and the rendered result. There is no second, hidden state: the
 * beginner surface, the expert panels, the AI planner and the renderer all
 * read and write this object and nothing else.
 *
 * Invariants (PLANT.md §7.1, `EDIT-01`, `AI-05`, `AI-07`, `AI-08`):
 *  - every field is a plain, inspectable number / small object,
 *  - every field is reversible by writing its default back,
 *  - nothing here can synthesize scene content; the renderer only re-weights
 *    light that the sensor already recorded.
 */

export const RECIPE_VERSION = 1 as const;

/** A single control point on a tone curve, in 0..1 input/output space. */
export interface CurvePoint {
  x: number;
  y: number;
}

export type CurveChannel = 'rgb' | 'red' | 'green' | 'blue';

export type HslBand =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'aqua'
  | 'blue'
  | 'purple'
  | 'magenta';

export const HSL_BANDS: readonly HslBand[] = [
  'red',
  'orange',
  'yellow',
  'green',
  'aqua',
  'blue',
  'purple',
  'magenta',
];

/** Centre hue in degrees for each HSL band, used by UI swatches and the shader. */
export const HSL_BAND_HUE: Record<HslBand, number> = {
  red: 0,
  orange: 30,
  yellow: 60,
  green: 120,
  aqua: 180,
  blue: 240,
  purple: 280,
  magenta: 320,
};

export interface HslAdjustment {
  /** Hue rotation within the band, -100..100 (maps to about ±30°). */
  hue: number;
  /** Saturation within the band, -100..100. */
  saturation: number;
  /** Luminance within the band, -100..100. */
  luminance: number;
}

export type GradingZone = 'shadows' | 'midtones' | 'highlights' | 'global';

export interface GradingWheel {
  /** Hue angle in degrees, 0..360. */
  hue: number;
  /** Saturation of the applied tint, 0..100. */
  saturation: number;
  /** Luminance offset for the zone, -100..100. */
  luminance: number;
}

export type MaskKind = 'radial' | 'linear' | 'luminance' | 'color';

/**
 * Local adjustments available inside a mask. Deliberately a subset of the
 * global controls so that a local edit is always explainable as "the same
 * control, applied where the mask is".
 */
export interface LocalAdjustments {
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  whites: number;
  blacks: number;
  temperature: number;
  tint: number;
  saturation: number;
  vibrance: number;
  clarity: number;
  texture: number;
  sharpness: number;
  noise: number;
}

export interface MaskLayer {
  id: string;
  name: string;
  kind: MaskKind;
  enabled: boolean;
  /** Invert the mask coverage. */
  invert: boolean;
  /** Softness of the mask edge, 0..100. */
  feather: number;
  /** Overall strength of this layer, 0..100. */
  opacity: number;

  /** Radial: normalised centre, radii and rotation of the ellipse. */
  radial: {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
    angle: number;
  };

  /** Linear: normalised start / end of the gradient axis. */
  linear: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };

  /** Luminance range mask, in 0..1 display luminance. */
  luminance: {
    low: number;
    high: number;
    lowFeather: number;
    highFeather: number;
  };

  /** Colour range mask: target hue in degrees plus tolerances. */
  color: {
    hue: number;
    hueRange: number;
    minSaturation: number;
  };

  adjustments: LocalAdjustments;

  /** Where this layer came from, for provenance display. */
  origin: 'manual' | 'ai';
  /** Plain-language reason, when the layer was proposed by the planner. */
  rationale?: string;
}

export interface CropRect {
  /** Normalised crop box against the oriented source, 0..1. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Recipe {
  version: typeof RECIPE_VERSION;

  // ---- White balance -----------------------------------------------------
  /**
   * Correlated colour temperature in Kelvin. `null` means "as shot": the
   * renderer uses the camera's recorded multipliers untouched.
   */
  temperature: number | null;
  /** Green / magenta tint, -150..150. `null` means as shot. */
  tint: number | null;

  // ---- Tone --------------------------------------------------------------
  /** Exposure in stops, -5..+5. */
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  whites: number;
  blacks: number;

  // ---- Presence ----------------------------------------------------------
  texture: number;
  clarity: number;
  dehaze: number;
  vibrance: number;
  saturation: number;

  // ---- Curves ------------------------------------------------------------
  curves: Record<CurveChannel, CurvePoint[]>;

  // ---- Colour mixer ------------------------------------------------------
  hsl: Record<HslBand, HslAdjustment>;

  // ---- Colour grading ----------------------------------------------------
  grading: Record<GradingZone, GradingWheel> & {
    /** How much the three zones overlap, 0..100. */
    blending: number;
    /** Shifts the shadow / highlight split point, -100..100. */
    balance: number;
  };

  // ---- Detail ------------------------------------------------------------
  sharpenAmount: number;
  sharpenRadius: number;
  sharpenDetail: number;
  sharpenMasking: number;
  noiseLuminance: number;
  noiseLuminanceDetail: number;
  noiseColor: number;

  // ---- Effects -----------------------------------------------------------
  vignetteAmount: number;
  vignetteMidpoint: number;
  vignetteFeather: number;
  vignetteRoundness: number;
  grainAmount: number;
  grainSize: number;

  // ---- Geometry ----------------------------------------------------------
  crop: CropRect | null;
  /** Straightening angle in degrees, -45..45. */
  rotate: number;
  /** Quarter-turns applied before straightening, 0..3. */
  orientation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;

  // ---- Local -------------------------------------------------------------
  masks: MaskLayer[];
}

export function defaultLocalAdjustments(): LocalAdjustments {
  return {
    exposure: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    whites: 0,
    blacks: 0,
    temperature: 0,
    tint: 0,
    saturation: 0,
    vibrance: 0,
    clarity: 0,
    texture: 0,
    sharpness: 0,
    noise: 0,
  };
}

export function defaultMask(kind: MaskKind, id: string, name: string): MaskLayer {
  return {
    id,
    name,
    kind,
    enabled: true,
    invert: false,
    feather: 50,
    opacity: 100,
    radial: { cx: 0.5, cy: 0.5, rx: 0.32, ry: 0.32, angle: 0 },
    linear: { x1: 0.5, y1: 0.15, x2: 0.5, y2: 0.6 },
    luminance: { low: 0, high: 0.5, lowFeather: 0.1, highFeather: 0.15 },
    color: { hue: 25, hueRange: 30, minSaturation: 0.08 },
    adjustments: defaultLocalAdjustments(),
    origin: 'manual',
  };
}

function neutralCurve(): CurvePoint[] {
  return [
    { x: 0, y: 0 },
    { x: 1, y: 1 },
  ];
}

export function defaultRecipe(): Recipe {
  return {
    version: RECIPE_VERSION,
    temperature: null,
    tint: null,
    exposure: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    whites: 0,
    blacks: 0,
    texture: 0,
    clarity: 0,
    dehaze: 0,
    vibrance: 0,
    saturation: 0,
    curves: {
      rgb: neutralCurve(),
      red: neutralCurve(),
      green: neutralCurve(),
      blue: neutralCurve(),
    },
    hsl: Object.fromEntries(
      HSL_BANDS.map((band) => [band, { hue: 0, saturation: 0, luminance: 0 }]),
    ) as Record<HslBand, HslAdjustment>,
    grading: {
      shadows: { hue: 0, saturation: 0, luminance: 0 },
      midtones: { hue: 0, saturation: 0, luminance: 0 },
      highlights: { hue: 0, saturation: 0, luminance: 0 },
      global: { hue: 0, saturation: 0, luminance: 0 },
      blending: 50,
      balance: 0,
    },
    sharpenAmount: 25,
    sharpenRadius: 1,
    sharpenDetail: 25,
    sharpenMasking: 0,
    noiseLuminance: 0,
    noiseLuminanceDetail: 50,
    noiseColor: 25,
    vignetteAmount: 0,
    vignetteMidpoint: 50,
    vignetteFeather: 50,
    vignetteRoundness: 0,
    grainAmount: 0,
    grainSize: 25,
    crop: null,
    rotate: 0,
    orientation: 0,
    flipHorizontal: false,
    flipVertical: false,
    masks: [],
  };
}

export function cloneRecipe(recipe: Recipe): Recipe {
  return structuredClone(recipe);
}

/** True when the recipe still equals the untouched default. */
export function isNeutral(recipe: Recipe): boolean {
  return JSON.stringify(recipe) === JSON.stringify(defaultRecipe());
}

/** Curve is neutral when it is the two-point identity line. */
export function isNeutralCurve(points: CurvePoint[]): boolean {
  return (
    points.length === 2 &&
    points[0].x === 0 &&
    points[0].y === 0 &&
    points[1].x === 1 &&
    points[1].y === 1
  );
}
